import type { CategoryGroup } from './extract-entity-refs';

export type CategoryUsage = {
  category: string;
  /** Serialised JSON length of everything under this category, in characters. */
  bytes: number;
  /** How many subscribed chart cards plot this breakdown. */
  usedByCards: number;
  isUsed: boolean;
  /**
   * The entity objects themselves — names, headline metrics — as opposed to a
   * breakdown nested inside them. Always counted as used: every chart reads
   * the entity it is plotting.
   */
  isEntityLevel: boolean;
};

export type ResponseUsage = {
  categories: CategoryUsage[];
  usedBytes: number;
  unusedBytes: number;
  /** 0–1 over the categorised bytes; 1 when nothing was categorised. */
  usedShare: number;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Every value at a path whose array steps may be `[*]` — a breakdown lives
 * once per entity, and refs are deduplicated across entities, so the ref paths
 * alone would count only the first entity's copy.
 */
const resolveAll = (payload: unknown, path: string): unknown[] => {
  let current: unknown[] = [payload];
  for (const segment of path.split('.')) {
    const name = segment.replace(/\[(\d+|\*)\]/g, '');
    if (name.length > 0) {
      current = current.map((value) =>
        isPlainObject(value) ? value[name] : undefined
      );
    }
    for (const step of segment.matchAll(/\[(\d+|\*)\]/g)) {
      const index = step[1];
      current = current.flatMap((value) => {
        if (!Array.isArray(value)) return [];
        return index === '*' ? value : [value[Number(index)]];
      });
    }
  }
  return current.filter((value) => value !== undefined);
};

const jsonLength = (value: unknown) => {
  try {
    return JSON.stringify(value)?.length ?? 0;
  } catch {
    return 0;
  }
};

/**
 * The container a ref sits in, generalised across entities:
 * `composition_v3.entities[0].metrics.gender[3]` →
 * `composition_v3.entities[*].metrics.gender`.
 */
const containerOf = (refPath: string) =>
  refPath.replace(/\[\d+\]/g, '[*]').replace(/\[\*\]$/, '');

/**
 * How much of a response the page actually plots. A category is used when a
 * subscribed card carries it as its breakdown (`data-chart-dimension`); the
 * entity level is always used. Sizes are JSON lengths, which is what a reader
 * comparing "used" to "unused" needs them to be relative to each other.
 */
export const assessResponseUsage = ({
  groups,
  payload,
  cardDimensions,
}: {
  groups: CategoryGroup[];
  payload: unknown;
  /** One entry per subscribed card: its breakdown, or undefined for none. */
  cardDimensions: (string | undefined)[];
}): ResponseUsage => {
  const containersByCategory = groups.map((group) => ({
    category: group.category,
    containers: [...new Set(group.refs.map((ref) => containerOf(ref.path)))],
  }));

  const rawBytes = containersByCategory.map(({ containers }) =>
    containers.reduce(
      (total, container) =>
        total +
        resolveAll(payload, container).reduce<number>(
          (sum, value) => sum + jsonLength(value),
          0
        ),
      0
    )
  );

  const nests = (outer: string, inner: string) =>
    inner !== outer &&
    (inner.startsWith(`${outer}.`) || inner.startsWith(`${outer}[`));

  const categories: CategoryUsage[] = containersByCategory.map(
    ({ category, containers }, index) => {
      const nestedIndexes = containersByCategory
        .map((other, otherIndex) =>
          otherIndex !== index &&
          other.containers.some((inner) =>
            containers.some((outer) => nests(outer, inner))
          )
            ? otherIndex
            : -1
        )
        .filter((otherIndex) => otherIndex !== -1);
      const isEntityLevel = nestedIndexes.length > 0;
      const bytes = Math.max(
        0,
        (rawBytes[index] ?? 0) -
          nestedIndexes.reduce(
            (sum, nested) => sum + (rawBytes[nested] ?? 0),
            0
          )
      );
      const usedByCards = isEntityLevel
        ? cardDimensions.length
        : cardDimensions.filter((dimension) => dimension === category).length;
      return {
        category,
        bytes,
        usedByCards,
        isUsed: isEntityLevel || usedByCards > 0,
        isEntityLevel,
      };
    }
  );

  const usedBytes = categories
    .filter((usage) => usage.isUsed)
    .reduce((sum, usage) => sum + usage.bytes, 0);
  const unusedBytes = categories
    .filter((usage) => !usage.isUsed)
    .reduce((sum, usage) => sum + usage.bytes, 0);
  const total = usedBytes + unusedBytes;

  return {
    categories,
    usedBytes,
    unusedBytes,
    usedShare: total === 0 ? 1 : usedBytes / total,
  };
};
